/* Hallmark · component: footer link columns · genre: editorial · theme: design.md-locked
 * states: default · hover · focus-visible · active (nav links — the remaining 4 states are N/A)
 * contrast: pass (muted-foreground on background, ring-token focus)
 */
import React from "react";
import { useContent } from "@thoughtbot/superglue";

import { BrandMark } from "@javascript/components/BrandMark";
import { openCookiePreferences } from "@javascript/components/CookieConsentBanner";

// `glyph` names a rule in host_ui.css, which draws the mark from the
// goldypaper-socials icon font. A new network is a glyph there, not a
// component here.
export const SOCIAL_LINKS = [
  { name: "Instagram", href: "https://instagram.com/goldypaper.stories", glyph: "instagram" },
  { name: "Facebook", href: "https://facebook.com/goldypaper", glyph: "facebook" },
] as const;

const FOOTER_COLUMNS = [
  {
    heading: "Product",
    links: [
      { label: "Home", href: "/" },
      { label: "Blog", href: "/blog" },
      { label: "Brand kit", href: "/brand-kit" },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "Contact", href: "/contact" },
      { label: "Log in", href: "/login" },
      { label: "Sign up", href: "/sign_up" },
    ],
  },
  {
    heading: "Legal",
    links: [
      { label: "Terms of Service", href: "/terms" },
      { label: "Privacy Policy", href: "/privacy" },
    ],
  },
] as const;

// Module scope: evaluated once in the SSR isolate and once in the browser.
const COPYRIGHT_YEAR = new Date().getFullYear();

type MarketingFooterContent = {
  currentUser?: { email: string };
  hideSharedHeader?: boolean;
};

type MarketingFooterProps = {
  illustration?: boolean;
  variant?: "columns" | "letter-close";
};

export function MarketingFooter({ illustration = true, variant = "columns" }: MarketingFooterProps) {
  const { currentUser, hideSharedHeader } = useContent<MarketingFooterContent>();

  // A signed-in host on a chrome page already has the compact host footer.
  if (currentUser && !hideSharedHeader) return null;
  if (variant === "letter-close") return <LetterCloseFooter />;

  return (
    <footer data-marketing-footer className="border-t bg-background">
      {illustration && <FooterIllustration />}

      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
        {/* 5 zones on desktop: 3 link groups + 2 intentionally empty for future links */}
        <div data-marketing-footer-columns className="grid gap-x-10 gap-y-8 py-10 sm:grid-cols-3 lg:grid-cols-5">
          {FOOTER_COLUMNS.map((column) => (
            <nav key={column.heading} aria-label={`Footer: ${column.heading}`}>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground">
                {column.heading}
              </h3>
              <ul className="mt-3 space-y-2 text-sm">
                {column.links.map((link) => (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      className="text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline hover:decoration-primary/50 focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:text-primary"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
                {column.heading === "Legal" && (
                  <li>
                    <button
                      type="button"
                      onClick={openCookiePreferences}
                      className="text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline hover:decoration-primary/50 focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:text-primary"
                    >
                      Cookie preferences
                    </button>
                  </li>
                )}
              </ul>
            </nav>
          ))}
        </div>

        <div
          data-marketing-footer-bottom
          className="flex flex-col gap-4 border-t py-6 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
            <a
              href="/"
              aria-label="Goldypaper home"
              className="font-serif font-semibold text-foreground"
            >
              <BrandMark size="sm" />
            </a>
            <p className="text-xs text-muted-foreground">© {COPYRIGHT_YEAR} Goldypaper</p>
          </div>

          <ul data-marketing-footer-social className="flex items-center gap-1">
            {SOCIAL_LINKS.map(({ name, href, glyph }) => (
              <li key={name}>
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={name}
                  className="inline-flex size-11 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground"
                >
                  <span aria-hidden="true" className={`social-icon social-icon-${glyph}`} />
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
}

function LetterCloseFooter() {
  const footerLinkClass = "inline-flex min-h-11 items-center whitespace-nowrap text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:text-primary";

  return (
    <footer data-marketing-footer className="border-t bg-background">
      <div className="mx-auto grid w-full max-w-6xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
        <div className="max-w-xl">
          <a
            href="/"
            aria-label="Goldypaper home"
            className="inline-flex min-h-11 items-center font-serif font-semibold text-foreground focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <BrandMark size="sm" />
          </a>
          <p className="mt-3 leading-relaxed text-muted-foreground">
            Online wedding invitations, composed with the care of something you would keep.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
          <p className="text-xs text-muted-foreground">© {COPYRIGHT_YEAR} Goldypaper</p>
          <nav aria-label="Brand kit footer" className="flex items-center gap-4">
            <a href="/" className={footerLinkClass}>Home</a>
            <a href="/contact" className={footerLinkClass}>Contact</a>
          </nav>
        </div>
      </div>
    </footer>
  );
}

function FooterIllustration() {
  return (
    <div
      aria-hidden
      className="pointer-events-none w-full text-primary [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]"
    >
      <svg
        viewBox="0 0 1440 180"
        preserveAspectRatio="xMidYMax meet"
        aria-hidden="true"
        focusable="false"
        className="w-full"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* rising sun, clear of the slopes in the upper-left sky */}
        <circle cx="1040" cy="52" r="12" strokeOpacity="0.5" />

        {/* clouds */}
        <path d="M208 36 a6 6 0 0 1 6 -6 a8 8 0 0 1 14 -2 a7 7 0 0 1 10 8 z" fill="currentColor" fillOpacity="0.05" strokeOpacity="0.5" />
        <path d="M392 30 a4 4 0 0 1 4 -4 a6 6 0 0 1 10 -2 a5 5 0 0 1 8 6 z" fill="currentColor" fillOpacity="0.05" strokeOpacity="0.5" />
        <path d="M636 26 a5 5 0 0 1 5 -5 a7 7 0 0 1 12 -2 a6 6 0 0 1 9 7 z" fill="currentColor" fillOpacity="0.05" strokeOpacity="0.5" />
        <path d="M1060 30 a6 6 0 0 1 6 -6 a8 8 0 0 1 14 -2 a7 7 0 0 1 10 8 z" fill="currentColor" fillOpacity="0.05" strokeOpacity="0.5" />

        {/* paper plane and its trail */}
        <path d="M344 46 h 26 M358 52 h 16" strokeOpacity="0.5" />
        <path d="M382 40 L414 30 L398 44 L393 52 Z M398 44 L414 30" fill="currentColor" fillOpacity="0.1" />

        {/* hot air balloon over Manhattan */}
        <circle cx="880" cy="22" r="7" fill="currentColor" fillOpacity="0.06" strokeOpacity="0.6" />
        <path d="M875 27 L877 33 M885 27 L883 33 M877 33 H883 V37 H877 Z" strokeOpacity="0.6" />

        {/* distant city mass — solid silhouettes, no stroke */}
        <g fill="currentColor" stroke="none">
          <path d="M0 156 V118 H58 V156 Z" opacity="0.06" />
          <path d="M66 156 V104 H124 V156 Z" opacity="0.06" />
          <path d="M248 156 V116 H306 V156 Z" opacity="0.06" />
          <path d="M332 156 V92 H398 V156 Z" opacity="0.06" />
          <path d="M432 156 V110 H498 V156 Z" opacity="0.06" />
          <path d="M520 156 V120 H560 V156 Z" opacity="0.06" />
          <path d="M646 156 V100 H706 V156 Z" opacity="0.06" />
          <path d="M730 156 V96 H780 V156 Z" opacity="0.06" />
          <path d="M920 156 V114 H986 V156 Z" opacity="0.06" />
          <path d="M1000 156 V108 H1052 V156 Z" opacity="0.06" />
          <path d="M1340 156 V106 H1396 V156 Z" opacity="0.06" />
          <path d="M1398 156 V118 H1440 V156 Z" opacity="0.06" />
        </g>

        {/* ——— Jakarta ——— */}
        {/* mid-rise pair, behind Monas */}
        <path d="M28 156 V100 H84 V156 Z" fill="currentColor" fillOpacity="0.06" strokeOpacity="0.5" />
        <path d="M46 110 V146 M66 110 V146" strokeOpacity="0.35" strokeDasharray="1 6" />
        <path d="M92 156 V118 H130 V156 Z" fill="currentColor" fillOpacity="0.06" strokeOpacity="0.5" />
        <path d="M106 126 V148 M118 126 V148" strokeOpacity="0.35" strokeDasharray="1 6" />
        <path d="M111 118 V110" strokeOpacity="0.5" />
        {/* Monas */}
        <path d="M150 156 V146 H194 V156 Z" fill="currentColor" fillOpacity="0.1" />
        <path d="M158 146 L163 80 H181 L186 146 Z" fill="currentColor" fillOpacity="0.1" />
        <path d="M152 80 Q 172 71 192 80 Q 172 89 152 80 Z" fill="currentColor" fillOpacity="0.1" />
        <path d="M172 71 V66" />
        <path d="M172 66 C 167 58 168 47 172 38 C 176 47 177 58 172 66 Z" fill="currentColor" stroke="none" />
        {/* Istiqlal dome */}
        <path d="M198 156 V134 H218 V156 Z" fill="currentColor" fillOpacity="0.08" />
        <path d="M201 134 Q 208 123 215 134 M208 123 V118" />
        {/* Wisma 46 */}
        <path d="M220 156 V88 H268 V156 Z" fill="currentColor" fillOpacity="0.1" />
        <path d="M220 88 L268 58 V88" />
        <path d="M232 100 V144 M244 100 V144 M256 100 V144" strokeOpacity="0.4" strokeDasharray="1 6" />
        {/* Jakarta mid-rise right */}
        <path d="M286 156 V110 H330 V156 Z" fill="currentColor" fillOpacity="0.06" strokeOpacity="0.5" />
        <path d="M300 110 V102 H316 V110" strokeOpacity="0.5" />
        <path d="M300 118 V148 M314 118 V148" strokeOpacity="0.35" strokeDasharray="1 6" />
        <path d="M348 156 V124 H384 V156 Z" fill="currentColor" fillOpacity="0.06" strokeOpacity="0.5" />
        <path d="M362 130 V148 M374 130 V148" strokeOpacity="0.35" strokeDasharray="1 6" />
        <path d="M366 124 V116" strokeOpacity="0.5" />
        <path d="M396 156 V122 H428 V156 Z" fill="currentColor" fillOpacity="0.06" strokeOpacity="0.5" />
        <path d="M408 128 V148 M420 128 V148" strokeOpacity="0.35" strokeDasharray="1 6" />
        <path d="M412 122 V112" strokeOpacity="0.5" />

        {/* ——— New York ——— */}
        <path d="M466 156 V112 H512 V156 Z" fill="currentColor" fillOpacity="0.06" strokeOpacity="0.5" />
        <path d="M478 120 V146 M502 120 V146" strokeOpacity="0.35" strokeDasharray="1 6" />
        {/* rooftop water tower */}
        <path d="M476 112 V102 H492 V112 M479 112 V117 M489 112 V117 M475 102 L484 95 L493 102" fill="currentColor" fillOpacity="0.08" />
        <path d="M516 156 V96 H538 V156 Z" fill="currentColor" fillOpacity="0.08" />
        <path d="M527 96 V86" />
        <path d="M527 106 V146" strokeOpacity="0.35" strokeDasharray="1 6" />
        {/* Empire State Building */}
        <path d="M542 156 V116 H618 V156 Z" fill="currentColor" fillOpacity="0.1" />
        <path d="M554 116 V92 H606 V116 Z" fill="currentColor" fillOpacity="0.1" />
        <path d="M564 92 V70 H596 V92 Z" fill="currentColor" fillOpacity="0.1" />
        <path d="M574 70 V54 H586 V70 Z" fill="currentColor" fillOpacity="0.1" />
        <path d="M580 54 V34" />
        <path d="M556 124 V148 M568 124 V148 M592 124 V148 M604 124 V148" strokeOpacity="0.4" strokeDasharray="1 6" />
        <path d="M626 156 V106 H660 V156 Z" fill="currentColor" fillOpacity="0.06" strokeOpacity="0.5" />
        <path d="M638 106 V98 H650 V106" strokeOpacity="0.5" />
        <path d="M636 114 V148 M650 114 V148" strokeOpacity="0.35" strokeDasharray="1 6" />
        {/* Chrysler Building */}
        <path d="M668 156 V92 H732 V156 Z" fill="currentColor" fillOpacity="0.1" />
        <path d="M676 92 A 24 30 0 0 1 724 92 Z" fill="currentColor" fillOpacity="0.1" />
        <path d="M684 92 A 16 21 0 0 1 716 92" />
        <path d="M692 92 A 8 12 0 0 1 708 92" />
        <path d="M700 62 V38" />
        <path d="M682 104 V148 M700 104 V148 M718 104 V148" strokeOpacity="0.4" strokeDasharray="1 6" />
        <path d="M740 156 V118 H764 V156 Z" fill="currentColor" fillOpacity="0.06" strokeOpacity="0.5" />
        <path d="M748 124 V148 M758 124 V148" strokeOpacity="0.35" strokeDasharray="1 6" />
        {/* One World Trade Center */}
        <path d="M772 156 L782 58 H842 L852 156 Z" fill="currentColor" fillOpacity="0.1" />
        <path d="M812 58 V30" />
        <path d="M786 84 H838 M784 108 H840 M782 132 H842" strokeOpacity="0.4" strokeDasharray="4 5" />
        <path d="M856 156 V126 H878 V156 Z" fill="currentColor" fillOpacity="0.06" strokeOpacity="0.5" />
        <path d="M864 132 V148 M872 132 V148" strokeOpacity="0.35" strokeDasharray="1 6" />
        {/* Statue of Liberty */}
        <path d="M884 156 V146 H920 V156 Z" fill="currentColor" fillOpacity="0.1" />
        <path d="M892 146 L896 114 H908 L912 146 Z" fill="currentColor" fillOpacity="0.1" />
        <circle cx="902" cy="108" r="4.5" />
        <path d="M895 105 L891 99 M902 103 V96 M909 105 L913 99" />
        <path d="M908 116 L918 100 M918 100 V93" />
        <circle cx="918" cy="90" r="2.5" fill="currentColor" stroke="none" />

        {/* ——— Japan ——— */}
        {/* chochin lantern by the pagoda */}
        <path d="M940 156 V136" />
        <ellipse cx="940" cy="128" rx="5" ry="7" fill="currentColor" fillOpacity="0.08" />
        <path d="M935 128 H945 M938 121 H942 M938 135 H942" />
        {/* five-story pagoda */}
        <path d="M956 156 V144 H980 V156 Z" fill="currentColor" fillOpacity="0.1" />
        <path d="M960 144 V136 H976 V136 M952 136 H984" />
        <path d="M962 136 V128 H974 V128 M955 128 H981" />
        <path d="M964 128 V120 H972 V120 M958 120 H978" />
        <path d="M966 120 V112 H970 V112 M961 112 H975" />
        <path d="M968 112 V88 M963 98 H973 M965 93 H971" />
        {/* Mount Fuji */}
        <path d="M996 156 Q 1070 74 1118 68 Q 1166 74 1240 156 Z" fill="currentColor" fillOpacity="0.08" />
        <path
          d="M1078 90 L1090 99 L1101 89 L1112 99 L1123 89 L1134 99 L1146 90 Q 1132 72 1112 70 Q 1092 72 1078 90 Z"
          className="fill-background"
          stroke="none"
        />
        <path d="M1078 90 L1090 99 L1101 89 L1112 99 L1123 89 L1134 99 L1146 90" strokeOpacity="0.7" />
        {/* forest at the foot of Fuji */}
        <path d="M1036 146 v 6 M1052 142 v 8 M1070 146 v 6 M1088 143 v 7 M1106 146 v 6 M1124 143 v 7 M1142 146 v 6 M1160 142 v 8 M1178 146 v 6 M1196 143 v 7 M1212 146 v 6" strokeOpacity="0.4" />
        {/* Tokyo mid-rises between Fuji and the tower */}
        <path d="M1244 156 V120 H1266 V156 Z" fill="currentColor" fillOpacity="0.06" strokeOpacity="0.5" />
        <path d="M1252 128 V148 M1260 128 V148" strokeOpacity="0.35" strokeDasharray="1 6" />
        {/* Tokyo Tower */}
        <path d="M1272 156 L1294 106 M1328 156 L1306 106" />
        <path d="M1281 138 L1319 118 M1319 138 L1281 118" strokeOpacity="0.7" />
        <path d="M1288 106 H1312" />
        <path d="M1294 106 L1298 72 M1306 106 L1302 72" />
        <path d="M1294 72 H1306 M1300 72 V46" />
        <circle cx="1300" cy="43" r="1.5" fill="currentColor" stroke="none" />
        <path d="M1332 156 V130 H1352 V156 Z" fill="currentColor" fillOpacity="0.06" strokeOpacity="0.5" />
        <path d="M1340 136 V148" strokeOpacity="0.35" strokeDasharray="1 6" />
        {/* torii gate */}
        <path d="M1368 156 L1370 124 M1396 156 L1394 124" />
        <path d="M1356 124 Q 1382 115 1408 124 M1356 124 L1353 120 M1408 124 L1411 120" />
        <path d="M1363 134 H1401 M1382 133 V126" />
        {/* sakura tree */}
        <path d="M1426 156 L1424 142 M1426 150 L1432 142" />
        <circle cx="1418" cy="134" r="7" fill="currentColor" fillOpacity="0.06" />
        <circle cx="1434" cy="132" r="8" fill="currentColor" fillOpacity="0.06" />
        <circle cx="1426" cy="123" r="7" fill="currentColor" fillOpacity="0.06" />
        {/* drifting sakura */}
        <circle cx="1262" cy="58" r="2" fill="currentColor" stroke="none" />
        <circle cx="1344" cy="66" r="2" fill="currentColor" stroke="none" />
        <circle cx="1396" cy="48" r="2" fill="currentColor" stroke="none" />
        <circle cx="1420" cy="84" r="2" fill="currentColor" stroke="none" />
        <circle cx="1240" cy="36" r="2" fill="currentColor" stroke="none" />
        <circle cx="1290" cy="40" r="2" fill="currentColor" stroke="none" />
        <circle cx="1224" cy="88" r="2" fill="currentColor" stroke="none" />
        <circle cx="1006" cy="66" r="2" fill="currentColor" stroke="none" />
        <path d="M1246 74 q 4 4 9 1" strokeOpacity="0.5" />

        {/* birds */}
        <path d="M116 42 q 5 -5 10 0 q 5 -5 10 0" strokeOpacity="0.6" />
        <path d="M424 56 q 4 -4 8 0 q 4 -4 8 0" strokeOpacity="0.6" />
        <path d="M742 28 q 4 -4 8 0 q 4 -4 8 0" strokeOpacity="0.6" />
        <path d="M900 34 q 4 -4 8 0 q 4 -4 8 0" strokeOpacity="0.6" />
        <path d="M1180 26 q 4 -4 8 0 q 4 -4 8 0" strokeOpacity="0.6" />

        {/* ground + reflections */}
        <path d="M0 156 H1440" strokeOpacity="0.7" />
        <path d="M28 162 h 40 M148 162 h 48 M296 168 h 36 M556 162 h 60 M690 168 h 44 M784 162 h 56 M948 168 h 40 M1060 162 h 72 M1240 168 h 44 M1280 162 h 40 M1350 168 h 48" strokeOpacity="0.4" />
      </svg>
    </div>
  );
}

